import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import SubscriptionService from "../../services/SubscriptionService";
import { useIsMobile } from "../../App";
import analyticsService from "../../services/AnalyticsService";

const SubscriptionScreen = () => {
  const { theme } = useTheme();
  const { t, currentLanguage } = useLocalization();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      analyticsService.logEvent("screen_view_web", {
        screen_name: "Subscription",
        screen_class: "SubscriptionScreen",
        timestamp: new Date().toISOString(),
      });
    } catch (_) {}
  }, []);

  // Завантаження планів при відкритті сторінки
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        setError(null);
        const response = await SubscriptionService.getPlans();
        
        // Як в мобільній версії - перевіряємо success і data
        if (response.success && response.data) {
          // Перевіряємо, чи дані в масиві або об'єкті (як в мобільній версії)
          const plansData = Array.isArray(response.data) 
            ? response.data 
            : (response.data.plans || response.data.data || []);
          
          // Маппимо дані з API до формату, який очікує компонент (точно як в мобільній версії)
          const mappedPlans = plansData.map(plan => {
            const searchesLimit = plan.searches_limit || 0;
            const planName = plan.name || '';
            
            // Прибираємо "(default)" з description
            const cleanDescription = (plan.description || '').replace(/\s*\(default\)/gi, '').trim();
            
            const rawPlanId = plan.id ?? plan.plan_id ?? plan.planId;
            const numericPlanId = Number(rawPlanId);
            const useNumericId = Number.isFinite(numericPlanId) && numericPlanId > 0;

            return {
              id: useNumericId ? numericPlanId : rawPlanId || planName,
              name: planName,
              description: cleanDescription,
              titleUk: plan.title_uk || plan.titleUk || planName,
              titleEn: plan.title_en || plan.titleEn || planName,
              titleRu: plan.title_ru || plan.titleRu || planName,
              titlePl: plan.title_pl || plan.titlePl || planName,
              amountUsd: parseFloat(plan.price || plan.amount_usd || plan.amountUsd || plan.amount || 0),
              currency: plan.currency || 'USD',
              durationDays: plan.duration_days || plan.durationDays || 31,
              searchesLimit: searchesLimit,
              promoDiscountPercent: plan.promo_discount_percent ? parseFloat(plan.promo_discount_percent) : null,
              promoSecondMonthPrice: plan.promo_second_month_price ? parseFloat(plan.promo_second_month_price) : null,
              promoDiscountedPrice: plan.promo_discounted_price ? parseFloat(plan.promo_discounted_price) : null,
              promoApplicationType: plan.promo_application_type || null,
              promoLabel: plan.promo_label || null,
            };
          });

          const normalizedPlans = mappedPlans.map(plan => ({
            ...plan,
            id: Number.isFinite(Number(plan.id)) ? Number(plan.id) : plan.id,
          }));

          setPlans(normalizedPlans);
          // Встановлюємо перший план як вибраний за замовчуванням
          if (normalizedPlans.length > 0 && !selectedPlanId) {
            const firstPlanId = normalizedPlans[0].id;
            setSelectedPlanId(Number.isFinite(Number(firstPlanId)) ? Number(firstPlanId) : firstPlanId);
          }

          try {
            analyticsService.logEvent("subscription_plans_loaded_web", {
              screen_name: "Subscription",
              plans_count: normalizedPlans.length,
              timestamp: new Date().toISOString(),
            });
          } catch (_) {}
        } else {
          // Якщо API не повернув дані, використовуємо дефолтні плани
          const defaultPlans = [
            {
              id: 1,
              titleUk: "10 запитань",
              titleEn: "10 questions",
              titleRu: "10 вопросов",
              titlePl: "10 pytań",
              amountUsd: 8.99,
            },
            {
              id: 2,
              titleUk: "Безлімітні запитання",
              titleEn: "Unlimited questions",
              titleRu: "Безлимит вопросов",
              titlePl: "Nielimitowane pytania",
              amountUsd: 19.99,
            },
          ];
          setPlans(defaultPlans);
          setSelectedPlanId(2);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
        setError(error.message || "Failed to fetch plans");
        // У випадку помилки використовуємо дефолтні плани
        const defaultPlans = [
          {
            id: 1,
            titleUk: "10 запитань",
            titleEn: "10 questions",
            titleRu: "10 вопросов",
            titlePl: "10 pytań",
            amountUsd: 8.99,
          },
          {
            id: 2,
            titleUk: "Безлімітні запитання",
            titleEn: "Unlimited questions",
            titleRu: "Безлимит вопросов",
            titlePl: "Nielimitowane pytania",
            amountUsd: 19.99,
          },
        ];
        setPlans(defaultPlans);
        setSelectedPlanId(2);

        try {
          analyticsService.logEvent("subscription_plans_error_web", {
            screen_name: "Subscription",
            error_message: error?.message || "Unknown error",
            timestamp: new Date().toISOString(),
          });
        } catch (_) {}
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const selectedPlan = useMemo(
    () =>
      plans.find((p) => Number(p.id) === Number(selectedPlanId)) || plans[0] || null,
    [plans, selectedPlanId]
  );

  const getPlanTitle = (plan) => {
    switch (currentLanguage) {
      case "uk":
        return plan.titleUk;
      case "ru":
        return plan.titleRu;
      case "pl":
        return plan.titlePl;
      default:
        return plan.titleEn;
    }
  };

  const formatPrice = (amount, currency = 'USD') => {
    const formattedAmount = amount.toFixed(2);
    if (currency === 'UAH') {
      return `${formattedAmount} ₴`;
    }
    return `USD ${formattedAmount}`;
  };

  const formatDuration = (days) => {
    if (days === 31) return "1 місяць";
    if (days === 365) return "1 рік";
    if (days > 365) {
      const years = Math.floor(days / 365);
      const remainingDays = days % 365;
      if (remainingDays === 0) {
        return `${years} ${years === 1 ? "рік" : years < 5 ? "роки" : "років"}`;
      }
      return `${years} ${years === 1 ? "рік" : years < 5 ? "роки" : "років"} ${remainingDays} дн.`;
    }
    return `${days} ${days === 1 ? "день" : days < 5 ? "дні" : "днів"}`;
  };

  const onBuyPress = async () => {
    // Як в мобільній версії
    if (!selectedPlan) {
      setError("Тариф не вибрано. Будь ласка, спробуйте ще раз.");
      return;
    }

    try {
      analyticsService.logEvent("button_click_web", {
        button_name: "buy_subscription",
        screen_name: "Subscription",
        plan_id: selectedPlan.id,
        plan_price: selectedPlan.amountUsd,
        timestamp: new Date().toISOString(),
      });
    } catch (_) {}

    const numericPlanId = Number(selectedPlan.id);
    if (!Number.isFinite(numericPlanId) || numericPlanId <= 0) {
      console.error("Invalid plan ID for payment link:", selectedPlan);
      setError(
        "Обраний тариф тимчасово недоступний. Будь ласка, спробуйте пізніше або зверніться в підтримку."
      );

      try {
        analyticsService.logEvent("subscription_error_web", {
          screen_name: "Subscription",
          error_type: "invalid_plan_id",
          plan_id: selectedPlan?.id,
          timestamp: new Date().toISOString(),
        });
      } catch (_) {}
      return;
    }

    try {
      setLoading(true);
      const res = await SubscriptionService.getPaymentLink(numericPlanId);
      console.log("getPaymentLink response:", JSON.stringify(res, null, 2));
      
      const url =
        (res && res.success && res.data && (res.data.url || res.data.link)) ||
        null;
      
      if (url) {
        try {
          analyticsService.logEvent("subscription_payment_link_success_web", {
            screen_name: "Subscription",
            plan_id: numericPlanId,
            plan_price: selectedPlan.amountUsd,
            timestamp: new Date().toISOString(),
          });
        } catch (_) {}

        const openedAt = new Date().toISOString();
        // Відкриваємо посилання на оплату в цій же вкладці, щоб уникнути дублювання вкладок
        try {
          window.location.assign(url);

          try {
            analyticsService.logEvent("subscription_payment_link_opened_web", {
              screen_name: "Subscription",
              plan_id: numericPlanId,
              opened_in_new_tab: false,
              timestamp: openedAt,
            });
          } catch (_) {}
        } catch (error) {
          console.error('Error opening payment link:', error);

          try {
            analyticsService.logEvent("subscription_payment_link_open_error_web", {
              screen_name: "Subscription",
              plan_id: numericPlanId,
              error_message: error?.message || 'Unknown error',
              timestamp: new Date().toISOString(),
            });
          } catch (_) {}
        }
      } else {
        // Обробка помилки, якщо посилання не отримано
        console.error('Payment link not received:', res);
        setError(res?.error || 'Payment link not received');

        try {
          analyticsService.logEvent("subscription_payment_link_error_web", {
            screen_name: "Subscription",
            plan_id: numericPlanId,
            error_type: "no_payment_link",
            timestamp: new Date().toISOString(),
          });
        } catch (_) {}
      }
    } catch (error) {
      console.error('Error getting payment link:', error);
      setError(error.message || 'Failed to get payment link');
      try {
        analyticsService.logEvent("subscription_payment_link_error_web", {
          screen_name: "Subscription",
          plan_id: numericPlanId,
          error_type: "api_error",
          error_message: error?.message || 'Unknown error',
          timestamp: new Date().toISOString(),
        });
      } catch (_) {}
    } finally {
      setLoading(false);
    }
  };

  const containerStyles = {
    width: "100%",
    maxWidth: "800px",
    margin: "0 auto",
    padding: isMobile ? "20px 16px 80px 16px" : "40px 24px",
    backgroundColor: theme.background,
    minHeight: isMobile ? "calc(100vh - 60px)" : "calc(100vh - 80px)",
  };

  const headerStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "32px",
  };

  const closeButtonStyles = {
    background: "none",
    border: "none",
    color: theme.text,
    fontSize: "24px",
    cursor: "pointer",
    padding: "8px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
  };

  const logoContainerStyles = {
    width: "100%",
    height: isMobile ? "120px" : "200px",
    borderRadius: "16px",
    backgroundColor: theme.surface,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: isMobile ? "16px" : "24px",
    border: `1px solid ${theme.border}`,
  };

  const titleStyles = {
    fontSize: isMobile ? "24px" : "32px",
    fontWeight: "700",
    color: theme.text,
    marginBottom: "8px",
  };

  const descriptionStyles = {
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.textSecondary,
    marginBottom: "24px",
  };

  const cardStyles = {
    border: `1px solid ${theme.border}`,
    borderRadius: "16px",
    padding: "16px",
    marginBottom: "24px",
    backgroundColor: theme.surface,
  };

  const planRowStyles = (selected) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    border: `1px solid ${selected ? theme.primary : theme.border}`,
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "12px",
    cursor: "pointer",
    backgroundColor: selected
      ? theme.isDark
        ? "#2a2a2a"
        : "#fff7f4"
      : "transparent",
    transition: "all 0.2s",
  });

  const radioStyles = (selected) => ({
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    border: `2px solid ${selected ? theme.primary : theme.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    flexShrink: 0,
    backgroundColor: selected ? theme.primary : "transparent",
  });

  const radioDotStyles = {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "#fff",
  };

  const planInfoStyles = {
    flex: 1,
    minWidth: 0,
  };

  const planTitleStyles = {
    fontSize: "16px",
    fontWeight: "600",
    color: theme.text,
    marginBottom: "4px",
  };

  const planDescriptionStyles = {
    fontSize: "12px",
    color: theme.textSecondary,
    marginTop: "2px",
    fontStyle: "italic",
  };

  const planHintStyles = {
    fontSize: "12px",
    color: theme.textSecondary,
    marginTop: "4px",
  };

  const planPriceStyles = {
    fontSize: "18px",
    fontWeight: "700",
    color: theme.text,
    marginLeft: "12px",
    flexShrink: 0,
  };

  const promoLabelStyles = {
    fontSize: "12px",
    fontWeight: "600",
    color: "#fff",
    backgroundColor: theme.primary,
    padding: "4px 8px",
    borderRadius: "6px",
    marginTop: "6px",
    display: "inline-block",
  };

  const buyButtonStyles = {
    width: "100%",
    padding: "16px",
    backgroundColor: theme.primary,
    color: "#fff",
    border: "none",
    borderRadius: "14px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: loading || plansLoading ? "not-allowed" : "pointer",
    opacity: loading || plansLoading ? 0.7 : 1,
    transition: "opacity 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  };

  const errorStyles = {
    color: theme.error,
    fontSize: "14px",
    marginTop: "12px",
    padding: "12px",
    backgroundColor: theme.isDark ? "#3a1a1a" : "#ffe6e6",
    borderRadius: "8px",
    border: `1px solid ${theme.error}`,
  };

  const loadingStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    flexDirection: "column",
    gap: "16px",
  };

  const spinnerStyles = {
    width: "40px",
    height: "40px",
    border: `4px solid ${theme.border}`,
    borderTopColor: theme.primary,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  };

  return (
    <div style={containerStyles}>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .plan-row:hover {
            border-color: ${theme.primary} !important;
            background-color: ${theme.isDark ? "#2a2a2a" : "#f5f5f5"} !important;
          }
          .close-btn:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f0f0f0"} !important;
          }
          .buy-btn:hover:not(:disabled) {
            opacity: 0.9 !important;
            transform: translateY(-1px);
          }
        `}
      </style>

      <div style={headerStyles}>
        <h1 style={titleStyles}>{t("subUnlimitedTitle")}</h1>
        <button
          className="close-btn"
          style={closeButtonStyles}
          onClick={() => {
            try {
              analyticsService.logEvent("button_click_web", {
                button_name: "close_subscription",
                screen_name: "Subscription",
                timestamp: new Date().toISOString(),
              });
            } catch (_) {}
            navigate(-1);
          }}
          title={t("close")}
        >
          ✕
        </button>
      </div>

      <div style={logoContainerStyles}>
        <img
          src={
            theme.isDark
              ? require("../../assets/logoProdLight.png")
              : require("../../assets/logoProd.png")
          }
          alt="NACHMED.AI"
          style={{
            maxWidth: isMobile ? "70%" : "90%",
            maxHeight: isMobile ? "70%" : "90%",
            objectFit: "contain",
          }}
        />
      </div>

      <p style={descriptionStyles}>{t("subNeedSubscriptionInfo")}</p>
      <p style={descriptionStyles}>{t("subChooseOne")}</p>

      {plansLoading ? (
        <div style={cardStyles}>
          <div style={loadingStyles}>
            <div style={spinnerStyles}></div>
            <p style={{ color: theme.textSecondary }}>
              {t("loading") || "Завантаження..."}
            </p>
          </div>
        </div>
      ) : (
        <div style={cardStyles}>
          {plans.map((plan) => {
            const selected = Number(plan.id) === Number(selectedPlanId);
            return (
              <div
                key={plan.id}
                className="plan-row"
                style={planRowStyles(selected)}
                onClick={() => {
                  try {
                    analyticsService.logEvent("subscription_plan_selected_web", {
                      screen_name: "Subscription",
                      plan_id: plan.id,
                      plan_name: plan.name,
                      plan_price: plan.amountUsd,
                      plan_duration_days: plan.durationDays,
                      timestamp: new Date().toISOString(),
                    });
                  } catch (_) {}
                  setSelectedPlanId(plan.id);
                }}
              >
                <div style={radioStyles(selected)}>
                  {selected && <div style={radioDotStyles} />}
                </div>
                <div style={planInfoStyles}>
                  <div style={planTitleStyles}>{getPlanTitle(plan)}</div>
                  {plan.description && (
                    <div style={planDescriptionStyles}>{plan.description}</div>
                  )}
                  {plan.promoLabel && (
                    <div style={promoLabelStyles}>{plan.promoLabel}</div>
                  )}
                  {plan.promoApplicationType === 'first_month' && plan.promoDiscountedPrice && (
                    <div style={planHintStyles}>
                      {t("subThenPrice", { amount: formatPrice(plan.amountUsd, plan.currency) })}
                    </div>
                  )}
                </div>
                <div style={planPriceStyles}>
                  {(plan.promoApplicationType === 'first_month' || plan.promoApplicationType === 'all_months') && plan.promoDiscountedPrice ? (
                    <>
                      <span style={{ textDecoration: 'line-through', opacity: 0.5, fontSize: '14px', marginRight: '6px' }}>
                        {formatPrice(plan.amountUsd, plan.currency)}
                      </span>
                      {formatPrice(plan.promoDiscountedPrice, plan.currency)}
                    </>
                  ) : (
                    formatPrice(plan.amountUsd, plan.currency)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {error && <div style={errorStyles}>{error}</div>}

      {selectedPlan && (
        <button
          className="buy-btn"
          style={buyButtonStyles}
          onClick={onBuyPress}
          disabled={loading || plansLoading}
        >
          {loading ? (
            <>
              <div style={spinnerStyles}></div>
              <span>{t("loading")}</span>
            </>
          ) : (
            t("subBuyFor", {
              amount: formatPrice(
                (selectedPlan.promoApplicationType === 'first_month' || selectedPlan.promoApplicationType === 'all_months') && selectedPlan.promoDiscountedPrice
                  ? selectedPlan.promoDiscountedPrice
                  : selectedPlan.amountUsd,
                selectedPlan.currency
              )
            })
          )}
        </button>
      )}
    </div>
  );
};

export default SubscriptionScreen;

